import classNames from "classnames";
import styles from "./styles.module.scss";
import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { steps } from "./mock";
import { handleStepChange, setupVideoEndListeners } from "./utils";

export interface UserGuideModalProps {
  readonly show: boolean;
  readonly setShow: (show: boolean) => void;
}

function UserGuideModal({ show, setShow }: UserGuideModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const closeModal = () => {
    setShow(!show);
  };

  useEffect(() => {
    if (show) {
      handleStepChange(0, setCurrentStep, videoRefs, true);
    } else {
      videoRefs.current.forEach((video) => video?.pause());
    }
  }, [show]);

  useEffect(() => {
    const cleanup = setupVideoEndListeners(
      videoRefs,
      steps.length,
      (nextIndex, playVideo) =>
        handleStepChange(nextIndex, setCurrentStep, videoRefs, playVideo),
    );
    return cleanup;
  }, []);

  const handleCloseKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      closeModal();
    }
  };

  return (
    <div className={classNames(styles.wrapper, { [styles.active]: show })}>
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.close}
          onClick={closeModal}
          onKeyDown={handleCloseKeyDown}
          aria-label="Close user guide"
        >
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.698 15.7673C17.1005 16.1636 17.1005 16.8063 16.698 17.2027C16.2956 17.5991 15.6431 17.5991 15.2407 17.2027L10.7286 12.7583C10.3262 12.3619 9.67369 12.3619 9.27126 12.7583L4.75913 17.2027C4.35671 17.5991 3.70425 17.5991 3.30182 17.2027C2.89939 16.8063 2.89939 16.1636 3.30182 15.7673L9.27126 9.88739C9.67369 9.491 10.3262 9.491 10.7286 9.88739L16.698 15.7673Z"
              fill="white"
              style={{ fillOpacity: 1 }}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.30198 5.23274C2.89955 4.83635 2.89955 4.19368 3.30198 3.79729C3.7044 3.4009 4.35686 3.4009 4.75929 3.79729L9.27142 8.24172C9.67385 8.63811 10.3263 8.63811 10.7287 8.24172L15.2409 3.79729C15.6433 3.4009 16.2958 3.4009 16.6982 3.79729C17.1006 4.19368 17.1006 4.83635 16.6982 5.23274L10.7287 11.1126C10.3263 11.509 9.67385 11.509 9.27142 11.1126L3.30198 5.23274Z"
              fill="white"
              style={{ fillOpacity: 1 }}
            />
          </svg>
        </button>

        <h2>AI Image Generator</h2>

        <div className={styles.row}>
          <nav className={styles.nav} aria-label="User guide steps">
            {steps.map((stepItem, idx) => (
              <button
                key={stepItem.step}
                type="button"
                className={classNames(styles.nav__button, {
                  [styles.active]: currentStep === idx,
                })}
                onClick={() => handleStepChange(idx, setCurrentStep, videoRefs)}
              >
                <span>{idx + 1}</span> {stepItem.step}
              </button>
            ))}
          </nav>

          <div className={styles.step__content}>
            {steps.map((stepItem, idx) => (
              <div
                key={stepItem.step}
                data-step
                data-index={idx}
                className={classNames(styles.step, {
                  [styles.visible]: currentStep === idx,
                })}
              >
                <video
                  ref={(el) => (videoRefs.current[idx] = el)}
                  src={String(stepItem.video)}
                  controls
                />
                <ul className={styles.step__description}>
                  {stepItem.description.map((desc) => (
                    <li key={desc}>{desc}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserGuideModal;
